import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { FundTitle } from 'src/components/Buy/BuyFlow/ExpandedFundList/FundTitle';
import { MaybeExpandedFundClassificationTypeHeader } from 'src/components/Buy/BuyFlow/ExpandedFundList/MaybeExpandedFundClassificationTypeHeader';
import DeductionList from 'src/components/DeductionList/DeductionList';
import StepExpanded from 'src/components/Shared/StepExpanded/StepExpanded';
import EarlyWithdrawalPenalty from 'src/components/Shared/EarlyWithdrawalPenalty/EarlyWithdrawalPenalty';
import { WithholdingPanel } from 'src/components/Shared/WithholdingPanel';
import { updateFederalWithholding } from 'src/redux/modules/sellTransaction/actions';
import { SellFund } from 'src/redux/modules/sellTransaction/types';
import { getMandatoryWithholdingStates } from 'src/redux/modules/stateWithholding/selectors';
import { EARLY_WITHDRAWAL_PENALTY_AGE, UNIT } from 'src/settings/settings';
import { getPersonalInformationData } from 'src/redux/modules/user/PersonalInformation/personalInformation.selectors';
import styles from './AutoSellWithholding.module.scss';
import {
  isCustomerWebViewEnabled,
  isSalesforceRep,
} from 'src/redux/modules/authentication/authentication.module.selectors';
import IraWithholdingWarning from '../../Sell/IraWithholdingWarning/IraWithholdingWarning';
import If from '../../Shared/If/If';

export interface AutoSellWithholdingProps {
  funds: SellFund[];
}

export const AutoSellWithholding: React.FC<AutoSellWithholdingProps> = ({
  funds,
}) => {
  const customerWebViewEnabled: boolean = useSelector(isCustomerWebViewEnabled);
  const isSalesforceRepEnabled: boolean = useSelector(isSalesforceRep);
  const userInformation = useSelector(getPersonalInformationData);
  const userAge = userInformation?.age || 0;
  const mandatoryWithholdingStates = useSelector(getMandatoryWithholdingStates);
  let atLeastLOneFundIsTaxable = false;
  return (
    <StepExpanded>
      <div
        className={classNames(
          styles['autosell-withholding-container'],
          'e2e-expanded-fund-list',
          'expanded-fund-list',
          userAge <= EARLY_WITHDRAWAL_PENALTY_AGE
            ? 'have-early-withdrawal-penality'
            : ''
        )}
      >
        {funds.map((fund, i) => {
          const lastFund = fund?.[i - 1] ?? {};
          const {
            agreementID,
            agreementNumber,
            formattedEntityNames,
            fundName,
            federalWithholdingApplied,
            federalWithholdingDollars,
            federalWithholdingPercent,
            hasEarlyWithdrawalPenalty,
            stateIsEstimate,
            stateWithholdingApplied,
            stateWithholdingDisabled,
            stateWithholdingDollars,
            stateWithholdingPercent,
            newFund,
            sweep,
            ticker,
            grossAmountDollars,
            grossUnit,
          } = fund;

          atLeastLOneFundIsTaxable =
            atLeastLOneFundIsTaxable || !fund?.nonRetirementLike;
          return (
            <React.Fragment key={agreementID}>
              <div
                className={
                  styles[
                    hasEarlyWithdrawalPenalty &&
                    userAge <= EARLY_WITHDRAWAL_PENALTY_AGE
                      ? 'fund-titleAndPenality-container'
                      : ''
                  ]
                }
              >
                <div>
                  <MaybeExpandedFundClassificationTypeHeader
                    {...{ fund, lastFund }}
                  />
                  <FundTitle
                    {...{
                      agreementID,
                      agreementNumber,
                      formattedEntityNames,
                      fundName,
                      isNewFund: newFund,
                      showSweep: sweep,
                      ticker,
                    }}
                    showTicker
                  />
                  <div className={styles['auto-sell-panels-container']}>
                    <div className={styles['auto-sell-withholding-container']}>
                      <WithholdingPanel>
                        <DeductionList
                          showTransactionFees
                          customerWebViewEnabled={customerWebViewEnabled}
                          isRetirementLike={!fund?.nonRetirementLike}
                          isSalesforceRep={isSalesforceRepEnabled}
                          showRedemptionAmountMessage={[
                            UNIT.PERCENT,
                            UNIT.SHARES,
                          ].includes(grossUnit)}
                          withholdingEditAction={updateFederalWithholding}
                          {...{
                            federalWithholdingApplied,
                            federalWithholdingDollars,
                            federalWithholdingPercent,
                            fund,
                            grossAmountDollars,
                            mandatoryWithholdingStates,
                            stateIsEstimate,
                            stateWithholdingApplied,
                            stateWithholdingDisabled,
                            stateWithholdingDollars,
                            stateWithholdingPercent,
                          }}
                        >
                          Take-Home Amount
                        </DeductionList>
                      </WithholdingPanel>
                    </div>
                  </div>
                </div>
                {hasEarlyWithdrawalPenalty &&
                  userAge <= EARLY_WITHDRAWAL_PENALTY_AGE && (
                    <div className={styles['right-panel']}>
                      <EarlyWithdrawalPenalty />
                    </div>
                  )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <div className={styles['important-note-withholding']}>
        Important Note: Income tax withholding is only applied for retirement
        accounts.
      </div>

      {!customerWebViewEnabled && isSalesforceRepEnabled && (
        <span>
          <If test={atLeastLOneFundIsTaxable}>
            <div
              className={
                !customerWebViewEnabled && isSalesforceRepEnabled
                  ? 'market-volatility-note-box'
                  : ''
              }
            >
              <div
                className={
                  !customerWebViewEnabled && isSalesforceRepEnabled
                    ? 'market-volatility-note'
                    : ''
                }
              >
                <IraWithholdingWarning
                  caller={null}
                  closeIraWithholdingWarning={null}
                  isSalesforceRepMessage={true}
                  setInputFocus={null}
                />
              </div>
            </div>
          </If>
        </span>
      )}
    </StepExpanded>
  );
};
